/*
 * Хук страницы «Авто-бот»: живой снимок состояния (периодический /status + реакция на
 * WS-события) и отправка идемпотентных команд движку. Команды — fire-and-forget в очередь
 * движка (§1.9); после отправки делаем короткий settle + рефреш статуса, чтобы UI отразил
 * эффект. Денежной логики тут нет (§1.2) — только команды и показ.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, getStatus, newCommandId, postCommand } from '@/lib/api'
import { CommandKind, type EngineState } from '@/lib/types'
import { useAppStore } from '@/store/useAppStore'

/** Период фонового опроса /status (живой PnL/связки/баланс). */
const POLL_MS = 4000
/** Пауза после команды перед рефрешем (движок обрабатывает команду асинхронно). */
const SETTLE_MS = 400

export interface AutobotHook {
  engine: EngineState | null
  /** Ключ выполняемой команды (для спиннера/блокировки конкретной кнопки), либо null. */
  pending: string | null
  error: string | null
  /** Отправить команду; вернёт true при успешной постановке в очередь. */
  run: (
    key: string,
    kind: CommandKind,
    payload?: Record<string, unknown>,
  ) => Promise<boolean>
  refresh: () => void
}

export function useAutobot(): AutobotHook {
  const engine = useAppStore((s) => s.engine)
  const applyEngineState = useAppStore((s) => s.applyEngineState)
  const eventStamp = useAppStore((s) => s.eventStamp)
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)
  const reqSeq = useRef(0)

  const handleError = useCallback((err: unknown) => {
    if (err instanceof DOMException && err.name === 'AbortError') return
    if (err instanceof ApiError) {
      if (err.status === 401) {
        useAppStore.getState().logout()
        return
      }
      if (mounted.current) setError(err.detail)
      return
    }
    if (mounted.current) setError('Не удалось связаться с движком')
  }, [])

  const refreshNow = useCallback(
    async (signal?: AbortSignal) => {
      const seq = ++reqSeq.current
      try {
        const state = await getStatus(signal)
        // Применяем только самый свежий ответ (анти-clobber) и гасим устаревшую ошибку
        // (как useParser): успешный фоновый рефреш снимает баннер прошлого сбоя.
        if (mounted.current && seq === reqSeq.current) {
          applyEngineState(state)
          setError(null)
        }
      } catch (err) {
        if (seq === reqSeq.current) handleError(err)
      }
    },
    [applyEngineState, handleError],
  )

  // Фоновый опрос статуса (пауза при скрытой вкладке).
  useEffect(() => {
    mounted.current = true
    const ctrl = new AbortController()
    void refreshNow(ctrl.signal)
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshNow()
    }, POLL_MS)
    return () => {
      mounted.current = false
      ctrl.abort()
      clearInterval(id)
    }
  }, [refreshNow])

  // Внеочередной рефреш на значимое WS-событие движка (старт/стоп/связка/алерт).
  useEffect(() => {
    if (eventStamp > 0) void refreshNow()
  }, [eventStamp, refreshNow])

  const run = useCallback(
    async (key: string, kind: CommandKind, payload: Record<string, unknown> = {}) => {
      if (!mounted.current) return false
      setPending(key)
      setError(null)
      try {
        await postCommand(newCommandId(key), kind, payload)
        await new Promise((r) => setTimeout(r, SETTLE_MS))
        await refreshNow()
        return true
      } catch (err) {
        handleError(err)
        return false
      } finally {
        if (mounted.current) setPending(null)
      }
    },
    [refreshNow, handleError],
  )

  return { engine, pending, error, run, refresh: () => void refreshNow() }
}
